import * as React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subDays, subMonths, subYears } from "date-fns";

type InfoItem = { value: string | string[]; badge?: boolean; className?: string; onClick?: () => void };
type RightBadge = { label: string; className?: string };

interface RaioXModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl?: string | null;
  entityTypeLabel: string;
  entityName: string;
  infoItems: InfoItem[];
  rightBadges?: RightBadge[];
  children?: React.ReactNode;
  contentClassName?: string;
  showPeriodControls?: boolean;
  initialFrom?: Date;
  initialTo?: Date;
  onPeriodChange?: (from: Date, to: Date) => void;
  renderContent?: (ctx: { periodRange: [Date, Date]; setLoading: (loading: boolean) => void }) => React.ReactNode;
}

export function RaioXModal({ open, onOpenChange, imageUrl, entityTypeLabel, entityName, infoItems, rightBadges = [], children, contentClassName, showPeriodControls, initialFrom, initialTo, onPeriodChange, renderContent }: RaioXModalProps) {
  const initials = (entityName || "").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const [imgError, setImgError] = React.useState(false);
  const showImage = !!(imageUrl && imageUrl.trim().length > 0) && !imgError;
  const defaultTo = React.useMemo(() => endOfDay(new Date()), []);
  const defaultFrom = React.useMemo(() => startOfDay(subDays(defaultTo, 6)), [defaultTo]);
  const [cadFrom, setCadFrom] = React.useState<Date>(initialFrom ?? defaultFrom);
  const [cadTo, setCadTo] = React.useState<Date>(initialTo ?? defaultTo);
  const [periodLabel, setPeriodLabel] = React.useState<string>("Últimos 7 dias");
  const [childLoading, setChildLoading] = React.useState(false);
  React.useEffect(() => { if (onPeriodChange) onPeriodChange(cadFrom, cadTo); }, [cadFrom, cadTo]);
  const renderInfo = (item: InfoItem, index: number) => {
    const values = Array.isArray(item.value) ? item.value : [item.value];
    return values.map((val, idx) => (
      <React.Fragment key={`${index}-${idx}`}>
        {item.badge ? (
          <Badge onClick={item.onClick} className={(item.className || "bg-muted text-foreground border border-gray-400 h-5 px-2 py-0 text-xs") + (item.onClick ? " cursor-pointer" : "")}>{val}</Badge>
        ) : (
          <span onClick={item.onClick} className={item.onClick ? "cursor-pointer" : undefined}>{val}</span>
        )}
        {idx < values.length - 1 && <span className="mx-1">•</span>}
      </React.Fragment>
    ));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] max-h-[85vh] p-0 overflow-hidden border-none">
        <div className="flex max-h-[85vh] flex-col">
          <DialogHeader className="bg-background px-6 py-4 border-b">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                {showImage ? (
                  <img
                    src={imageUrl as string}
                    alt={entityName}
                    className="h-14 w-14 rounded-full object-cover border"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-muted border flex items-center justify-center text-lg font-semibold">{initials || ""}</div>
                )}
                <div className="min-w-0">
                  <DialogTitle className="sr-only">{`RAIO-X • ${entityTypeLabel} • ${entityName}`}</DialogTitle>
                  <DialogDescription className="sr-only">Visualização detalhada</DialogDescription>
                  <div className="text-xl">
                    <span className="text-2xl font-bold">RAIO-X</span>
                    {` • ${entityTypeLabel} • `}
                    <span className="font-bold">{entityName}</span>
                  </div>
                  <div className="text-sm text-muted-foreground truncate flex items-center gap-1 flex-wrap">
                    {infoItems.map((item, i) => (
                      <React.Fragment key={i}>
                        {renderInfo(item, i)}
                        {i < infoItems.length - 1 && <span className="mx-1">•</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
              {(showPeriodControls !== false) && (
                <div className="flex items-center gap-3 ml-8 mr-6">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="flex items-center gap-2">
                        <span>Período</span>
                        <span className="font-bold text-blue-600">{periodLabel}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center">
                      <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(now)); setCadTo(endOfDay(now)); setPeriodLabel("Hoje"); }}>Hoje</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const y = subDays(new Date(), 1); setCadFrom(startOfDay(y)); setCadTo(endOfDay(y)); setPeriodLabel("Ontem"); }}>Ontem</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const d = subDays(new Date(), 2); setCadFrom(startOfDay(d)); setCadTo(endOfDay(d)); setPeriodLabel("Antes de ontem"); }}>Antes de ontem</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfWeek(now, { weekStartsOn: 1 })); setCadTo(endOfWeek(now, { weekStartsOn: 1 })); setPeriodLabel("Esta semana"); }}>Esta semana</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const ref = subDays(new Date(), 7); setCadFrom(startOfWeek(ref, { weekStartsOn: 1 })); setCadTo(endOfWeek(ref, { weekStartsOn: 1 })); setPeriodLabel("Semana passada"); }}>Semana passada</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const ref = subDays(new Date(), 14); setCadFrom(startOfWeek(ref, { weekStartsOn: 1 })); setCadTo(endOfWeek(ref, { weekStartsOn: 1 })); setPeriodLabel("Semana retrasada"); }}>Semana retrasada</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfMonth(now)); setCadTo(endOfMonth(now)); setPeriodLabel("Este mês"); }}>Este mês</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const ref = subMonths(new Date(), 1); setCadFrom(startOfMonth(ref)); setCadTo(endOfMonth(ref)); setPeriodLabel("Mês passado"); }}>Mês passado</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const ref = subMonths(new Date(), 2); setCadFrom(startOfMonth(ref)); setCadTo(endOfMonth(ref)); setPeriodLabel("Mês retrasado"); }}>Mês retrasado</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfYear(now)); setCadTo(endOfYear(now)); setPeriodLabel("Este ano"); }}>Este ano</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const ref = subYears(new Date(), 1); setCadFrom(startOfYear(ref)); setCadTo(endOfYear(ref)); setPeriodLabel("Ano passado"); }}>Ano passado</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const ref = subYears(new Date(), 2); setCadFrom(startOfYear(ref)); setCadTo(endOfYear(ref)); setPeriodLabel("Ano retrasado"); }}>Ano retrasado</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(subDays(now, 2))); setCadTo(endOfDay(now)); setPeriodLabel("Últimos 3 dias"); }}>Últimos 3 dias</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(subDays(now, 6))); setCadTo(endOfDay(now)); setPeriodLabel("Últimos 7 dias"); }}>Últimos 7 dias</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(subDays(now, 14))); setCadTo(endOfDay(now)); setPeriodLabel("Últimos 15 dias"); }}>Últimos 15 dias</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(subDays(now, 29))); setCadTo(endOfDay(now)); setPeriodLabel("Últimos 30 dias"); }}>Últimos 30 dias</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(subDays(now, 59))); setCadTo(endOfDay(now)); setPeriodLabel("Últimos 60 dias"); }}>Últimos 60 dias</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { const now = new Date(); setCadFrom(startOfDay(subDays(now, 89))); setCadTo(endOfDay(now)); setPeriodLabel("Últimos 90 dias"); }}>Últimos 90 dias</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <div className="flex items-center gap-2">
                    <Input type="date" value={format(cadFrom, 'yyyy-MM-dd')} onChange={(e) => setCadFrom(startOfDay(new Date(e.target.value)))} />
                    <span className="text-muted-foreground">até</span>
                    <Input type="date" value={format(cadTo, 'yyyy-MM-dd')} onChange={(e) => setCadTo(endOfDay(new Date(e.target.value)))} />
                    {childLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 shrink-0">
                {rightBadges.length > 0 && (
                  <div className="flex items-center gap-2">
                    {rightBadges.map((b, i) => (
                      <Badge key={i} className={b.className || "uppercase mr-2"}>{b.label}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogHeader>
          <div className={`flex-1 overflow-auto ${contentClassName ?? "px-4 pb-4 pt-2"}`}>{renderContent ? renderContent({ periodRange: [cadFrom, cadTo], setLoading: (v) => setChildLoading(!!v) }) : children}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
